import React from "react";
import { motion } from "framer-motion";
import { Users, Clock, Phone, Ban, Check, Navigation } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { FLEET, PHONE_RAW, MAPS_DIRECTIONS_URL } from "../lib/data";

export const Fleet = () => {
  const { t, lang } = useLanguage();
  const eurPrimary = lang !== "cz";

  return (
    <section
      id="fleet"
      data-testid="fleet-section"
      className="relative py-24 md:py-32 bg-gradient-to-b from-sky-50 via-white to-sky-50/40"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="max-w-3xl">
          <span
            data-testid="fleet-eyebrow"
            className="inline-flex text-[11px] tracking-[0.2em] uppercase text-sky-700 font-semibold rounded-full bg-sky-100 px-4 py-1.5"
          >
            {t.fleet.eyebrow}
          </span>
          <h2
            data-testid="fleet-title"
            className="font-heading mt-5 text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-slate-900"
          >
            {t.fleet.title}
          </h2>
          <p className="mt-5 text-slate-600 text-base md:text-lg leading-relaxed max-w-2xl">
            {t.fleet.subtitle}
          </p>
        </div>

        <div
          id="pricing"
          data-testid="fleet-grid"
          className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5 md:gap-6"
        >
          {FLEET.map((boat, i) => {
            const item = t.fleet.items[boat.key];
            const spanClass =
              i === 0
                ? "lg:col-span-7"
                : i === 1
                ? "lg:col-span-5"
                : "lg:col-span-4";

            const primaryAmount = eurPrimary ? boat.priceEur : boat.price;
            const primaryUnit = eurPrimary ? "€" : "Kč";
            const secondaryAmount = eurPrimary ? boat.price : boat.priceEur;
            const secondaryUnit = eurPrimary ? "Kč" : "€";

            return (
              <motion.article
                key={boat.key}
                data-testid={`fleet-card-${boat.key}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                className={`group ${spanClass} relative overflow-hidden rounded-3xl bg-white border border-sky-100 shadow-[0_8px_32px_rgba(2,132,199,0.08)] hover:shadow-[0_20px_50px_rgba(2,132,199,0.18)] hover:-translate-y-1 transition-all duration-500`}
              >
                <div className="relative aspect-[4/3] md:aspect-[16/10] overflow-hidden">
                  <img
                    src={boat.image}
                    alt={item.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms] ease-out"
                    loading="lazy"
                  />
                  {boat.showTag && (
                    <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-yellow-400 text-slate-900 px-3 py-1 text-[11px] font-semibold tracking-wide shadow-md">
                      {item.tag}
                    </div>
                  )}
                  {(() => {
                    const swimLabel =
                      boat.swimType === "pedal"
                        ? t.nav.swimPedal
                        : t.nav.swimBoat;
                    const isAllowed = boat.swim;
                    const Icon = isAllowed ? Check : Ban;
                    const colorCls = isAllowed
                      ? "bg-emerald-500 text-white"
                      : "bg-red-500 text-white";
                    return (
                      <div
                        data-testid={`fleet-swim-${boat.key}`}
                        data-swim={isAllowed ? "yes" : "no"}
                        title={swimLabel}
                        className={`absolute top-4 right-4 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide shadow-lg ${colorCls}`}
                      >
                        <Icon className="w-3.5 h-3.5" strokeWidth={2.8} />
                        <span>{swimLabel}</span>
                      </div>
                    );
                  })()}
                </div>

                <div className="p-6 md:p-7">
                  <h3 className="font-heading text-slate-900 text-xl md:text-2xl leading-tight tracking-tight mb-4">
                    {item.name}
                  </h3>
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-4 text-slate-600 text-sm">
                      <span className="inline-flex items-center gap-1.5">
                        <Users className="w-4 h-4 text-sky-600" strokeWidth={2.5} />
                        {boat.capacity}{" "}
                        {boat.capacity >= 5 ? t.fleet.capacity5 : t.fleet.capacity}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="w-4 h-4 text-sky-600" strokeWidth={2.5} />
                        60 min
                      </span>
                    </div>
                    <div className="text-right">
                      <div
                        data-testid={`fleet-price-primary-${boat.key}`}
                        className="font-heading text-2xl md:text-3xl font-semibold tracking-tight text-slate-900"
                      >
                        {primaryAmount}{" "}
                        <span className="text-sm font-medium text-slate-500">
                          {primaryUnit} {t.fleet.perHour}
                        </span>
                      </div>
                      <div className="text-xs text-slate-400">
                        {secondaryAmount} {secondaryUnit} {t.fleet.perHour}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>

        <div className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
          <a
            href={`tel:${PHONE_RAW}`}
            data-testid="fleet-call-cta"
            className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white px-7 py-4 text-base font-semibold shadow-[0_12px_30px_rgba(15,23,42,0.25)] hover:-translate-y-0.5 transition-all"
          >
            <Phone className="w-5 h-5 text-yellow-300" strokeWidth={2.5} />
            {t.hero.ctaCall}
          </a>
          <a
            href={MAPS_DIRECTIONS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="fleet-directions-cta"
            className="inline-flex items-center gap-2 rounded-full bg-white hover:bg-sky-50 text-slate-900 px-7 py-4 text-base font-semibold border border-sky-200 shadow-sm hover:-translate-y-0.5 transition-all"
          >
            <Navigation className="w-5 h-5 text-sky-600" strokeWidth={2.5} />
            {t.nav.directions}
          </a>
        </div>
      </div>
    </section>
  );
};

export default Fleet;
